/**
 * useContactEmails — fetches the caller's ContactEmail rows.
 *
 * Email Fluidity Doctrine:
 *   - One Contact may hold many emails over time.
 *   - Connections form (connectedAt) and dissolve (disconnectedAt).
 *   - The body of memory holds the full history; the active set is what
 *     The Line currently knows about you.
 *
 * RLS limits visibility: the policy `contact_email_self_read` returns
 * only the rows whose contactId belongs to the caller's auth user.
 *
 * Mutations go through SECURITY DEFINER RPCs:
 *   - core.add_contact_email(p_email)
 *   - core.disconnect_contact_email(p_email_id, p_reason)
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface ContactEmailRow {
  id: string;
  contactId: string;
  email: string;
  isPrimary: boolean;
  verifiedAt: string | null;
  connectedAt: string;
  disconnectedAt: string | null;
  disconnectedReason: string | null;
  /** Phase J: when not null, a verification email was sent and is awaiting click */
  verifyTokenHash: string | null;
  tokenExpiresAt: string | null;
  verificationSentAt: string | null;
}

const EMAIL_COLUMNS =
  'id, contactId, email, isPrimary, verifiedAt, connectedAt, disconnectedAt, disconnectedReason, verifyTokenHash, tokenExpiresAt, verificationSentAt';

interface UseContactEmailsState {
  loading: boolean;
  emails: ContactEmailRow[];
  error: Error | null;
}

export function useContactEmails() {
  const { contact } = useAuth();
  const [state, setState] = useState<UseContactEmailsState>({
    loading: true,
    emails: [],
    error: null,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!contact) {
      setState({ loading: false, emails: [], error: null });
      return;
    }

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('ContactEmail')
        .select(EMAIL_COLUMNS)
        .eq('contactId', contact.id)
        .order('isPrimary', { ascending: false })
        .order('connectedAt', { ascending: true });

      if (cancelled) return;
      if (error) {
        setState({ loading: false, emails: [], error: new Error(error.message) });
        return;
      }
      setState({
        loading: false,
        emails: (data ?? []) as ContactEmailRow[],
        error: null,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [contact, refreshKey]);

  return { ...state, refresh };
}

/** Helper: split into currently-active + historical (disconnected) emails. */
export function splitEmailsByConnection(emails: ContactEmailRow[]): {
  active: ContactEmailRow[];
  history: ContactEmailRow[];
} {
  const active: ContactEmailRow[] = [];
  const history: ContactEmailRow[] = [];
  for (const e of emails) {
    if (e.disconnectedAt) history.push(e);
    else active.push(e);
  }
  return { active, history };
}
